import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import Utils from '../../Utils/Utils';
import './ImportLogsDrawer.css';

const TEAL = '#005E73';

class ImportLogsDrawer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      logs: [],
      expanded: {}, // logId -> bool
      deletingId: null,
      clearOpen: false,
      clearing: false,
    };
  }

  componentDidUpdate(prevProps) {
    // Refresh the logs each time the drawer is opened.
    if (this.props.open && !prevProps.open) {
      this.loadLogs();
    }
  }

  loadLogs = () => {
    this.setState({ loading: true });
    this.props.listImportLogs((status, data) => {
      if (status === 'success') {
        this.setState({ logs: Array.isArray(data) ? data : [], loading: false });
      } else {
        this.setState({ loading: false });
        this.props.customFeedBackBar('error', data || 'Failed to load import logs.');
      }
    });
  };

  toggleExpand = (logId) => {
    this.setState((prev) => ({
      expanded: { ...prev.expanded, [logId]: !prev.expanded[logId] },
    }));
  };

  handleDelete = (logId, e) => {
    if (e) e.stopPropagation();
    this.setState({ deletingId: logId });
    this.props.deleteImportLog({ logId }, (status, message) => {
      this.setState({ deletingId: null });
      if (status === 'success') {
        this.setState((prev) => ({
          logs: prev.logs.filter((l) => l.logId !== logId),
        }));
        this.props.customFeedBackBar('success', message || 'Log deleted.');
      } else {
        this.props.customFeedBackBar('error', message || 'Failed to delete log.');
      }
    });
  };

  openClearConfirm = () => this.setState({ clearOpen: true });
  closeClearConfirm = () => {
    if (this.state.clearing) return;
    this.setState({ clearOpen: false });
  };

  handleClearAll = () => {
    this.setState({ clearing: true });
    this.props.clearImportLogs((status, message) => {
      this.setState({ clearing: false });
      if (status === 'success') {
        this.setState({ logs: [], clearOpen: false, expanded: {} });
        this.props.customFeedBackBar('success', message || 'All logs cleared.');
      } else {
        this.props.customFeedBackBar('error', message || 'Failed to clear logs.');
      }
    });
  };

  renderStatusChip = (status) => {
    const isFailed = status === 'FAILED';
    const isPartial = status === 'PARTIAL';
    const label = isFailed ? 'Failed' : isPartial ? 'Partial' : 'Warning';
    return (
      <Chip
        size="small"
        icon={
          isFailed ? (
            <ErrorOutlineRoundedIcon style={{ fontSize: 15 }} />
          ) : (
            <WarningAmberRoundedIcon style={{ fontSize: 15 }} />
          )
        }
        label={label}
        className={`import-log-chip ${isFailed ? 'import-log-chip-failed' : 'import-log-chip-partial'}`}
      />
    );
  };

  renderLogCard = (log) => {
    const isOpen = !!this.state.expanded[log.logId];
    const details = log.details || [];
    const when = log.importDate ? Utils.formatDateTime(log.importDate) : '';

    return (
      <div key={log.logId} className="import-log-card">
        <div
          className="import-log-card-head"
          onClick={() => details.length && this.toggleExpand(log.logId)}
          style={{ cursor: details.length ? 'pointer' : 'default' }}
        >
          <div className="import-log-card-main">
            <div className="import-log-card-title-row">
              <span className="import-log-marketplace">{log.marketplace}</span>
              {this.renderStatusChip(log.status)}
            </div>
            {log.filename && (
              <div className="import-log-filename" title={log.filename}>
                <InsertDriveFileOutlinedIcon className="import-log-file-icon" />
                {log.filename}
              </div>
            )}
            {log.summary && <div className="import-log-summary">{log.summary}</div>}
            <div className="import-log-meta-row">
              {when && <span className="import-log-time">{when}</span>}
              {log.failedRowCount != null && (
                <span className="import-log-dot">•</span>
              )}
              {log.failedRowCount != null && (
                <span className="import-log-failcount">
                  {log.failedRowCount}
                  {log.totalRows != null ? ` / ${log.totalRows}` : ''} row
                  {log.failedRowCount === 1 ? '' : 's'} failed
                </span>
              )}
            </div>
          </div>

          <div className="import-log-card-actions">
            <Tooltip title="Delete log" arrow>
              <span>
                <IconButton
                  size="small"
                  className="import-log-delete-btn"
                  disabled={this.state.deletingId === log.logId}
                  onClick={(e) => this.handleDelete(log.logId, e)}
                >
                  {this.state.deletingId === log.logId ? (
                    <CircularProgress size={16} sx={{ color: '#d32f2f' }} />
                  ) : (
                    <DeleteOutlineIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            {details.length > 0 && (
              <ExpandMoreIcon
                className={`import-log-expand ${isOpen ? 'import-log-expand-open' : ''}`}
              />
            )}
          </div>
        </div>

        {details.length > 0 && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <div className="import-log-details">
              {details.map((line, idx) => (
                <div key={idx} className="import-log-detail-line">
                  {line}
                </div>
              ))}
            </div>
          </Collapse>
        )}
      </div>
    );
  };

  renderBody = () => {
    if (this.state.loading) {
      return (
        <div className="import-logs-state">
          <CircularProgress sx={{ color: TEAL }} />
        </div>
      );
    }

    if (!this.state.logs.length) {
      return (
        <div className="import-logs-state">
          <TaskAltRoundedIcon className="import-logs-empty-icon" />
          <div className="import-logs-empty-title">No import errors</div>
          <div className="import-logs-empty-sub">
            Failed or partial imports will appear here so you can review what
            went wrong.
          </div>
        </div>
      );
    }

    return (
      <div className="import-logs-list">
        {this.state.logs.map((log) => this.renderLogCard(log))}
      </div>
    );
  };

  render() {
    const { logs } = this.state;
    return (
      <Drawer
        anchor="right"
        open={this.props.open}
        onClose={this.props.onClose}
        PaperProps={{ className: 'import-logs-drawer-paper' }}
      >
        <div className="import-logs-header">
          <div className="import-logs-header-title">
            <ErrorOutlineRoundedIcon className="import-logs-header-icon" />
            <div>
              <div className="import-logs-header-text">Import Logs</div>
              <div className="import-logs-header-sub">
                {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
              </div>
            </div>
          </div>
          <div className="import-logs-header-actions">
            <Tooltip title="Refresh" arrow>
              <IconButton
                size="small"
                className="import-logs-header-btn"
                onClick={this.loadLogs}
                disabled={this.state.loading}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear all logs" arrow>
              <span>
                <IconButton
                  size="small"
                  className="import-logs-header-btn"
                  onClick={this.openClearConfirm}
                  disabled={this.state.loading || !logs.length}
                >
                  <DeleteSweepOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Close" arrow>
              <IconButton
                size="small"
                className="import-logs-header-btn"
                onClick={this.props.onClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <div className="import-logs-body">{this.renderBody()}</div>

        {/* Clear-all confirmation */}
        <Dialog
          open={this.state.clearOpen}
          onClose={this.closeClearConfirm}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 420,
              maxWidth: 480,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 2.5 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'rgba(211, 47, 47, 0.1)',
                color: '#d32f2f',
                flexShrink: 0,
              }}
            >
              <DeleteSweepOutlinedIcon />
            </Box>
            <Box sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
              Clear all import logs
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <DialogContentText sx={{ color: 'text.primary', fontSize: '0.95rem' }}>
              This will permanently remove all {logs.length} import log
              {logs.length === 1 ? '' : 's'}. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={this.closeClearConfirm}
              disabled={this.state.clearing}
              sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              onClick={this.handleClearAll}
              variant="contained"
              disabled={this.state.clearing}
              sx={{
                bgcolor: '#d32f2f',
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#b71c1c', boxShadow: 'none' },
              }}
            >
              {this.state.clearing ? 'Clearing...' : 'Clear all'}
            </Button>
          </DialogActions>
        </Dialog>
      </Drawer>
    );
  }
}

export default ImportLogsDrawer;
